import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SEVERITY, fmtTimeTz, timeAgo } from "@/lib/fleet";
import { EP_TYPES, valSummary, EpisodeDrawer } from "@/pages/AlertEpisodesView";
import { Check, ChevronRight, ChevronDown, ShieldCheck, Loader2, AlertCircle, SlidersHorizontal, RefreshCw } from "lucide-react";

const SEV_ORDER = { high: 3, medium: 2, low: 1 };
const typeLabel = (t) => EP_TYPES[t] || t;
// Dernière activité connue de la situation (l'épisode agrège déjà les répétitions via occurrence_count).
const lastAt = (ep) => (ep.updated_at && new Date(ep.updated_at) > new Date(ep.started_at) ? ep.updated_at : ep.started_at);

export default function AlertTriageView({ onGoHistory }) {
  const { user } = useAuth();
  const canAck = user && ["admin", "gestionnaire"].includes(user.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [detail, setDetail] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [moreFilters, setMoreFilters] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [pools, setPools] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [fCompany, setFCompany] = useState("");
  const [fPool, setFPool] = useState("");
  const [fVehicle, setFVehicle] = useState("");
  const [fType, setFType] = useState("");
  const [fSeverity, setFSeverity] = useState("");
  const [fAck, setFAck] = useState("all");

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  const params = useCallback(() => {
    const p = { status: "open", limit: "200" };
    if (fCompany) p.company_id = fCompany;
    if (fPool) p.pool_id = fPool;
    if (fVehicle) p.vehicle_id = fVehicle;
    if (fType) p.type = fType;
    if (fSeverity) p.severity = fSeverity;
    if (fAck !== "all") p.acknowledged = fAck;
    return p;
  }, [fCompany, fPool, fVehicle, fType, fSeverity, fAck]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await api.get(`/alert-episodes?${new URLSearchParams(params())}`);
      setItems(r.data || []);
    } catch { setError(true); setItems([]); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (detail) return;
    const id = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(id);
  }, [load, detail]);

  const ack = async (ep, e) => {
    e?.stopPropagation();
    try {
      const r = await api.post(`/alert-episodes/${ep.id}/ack`);
      setItems((prev) => prev.map((x) => (x.id === ep.id ? { ...x, ...r.data } : x)));
      if (detail?.id === ep.id) setDetail((d) => ({ ...d, ...r.data }));
      toast.success("Épisode acquitté");
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Acquittement non autorisé" : "Erreur");
    }
  };

  // Une SITUATION = un épisode ouvert, identité (véhicule, type, règle). Les répétitions d'une même situation
  // sont déjà agrégées par le backend dans occurrence_count. Deux situations indépendantes de même type sur un
  // même véhicule ont des rule_id différents -> deux épisodes distincts -> deux lignes distinctes (jamais fusionnées).
  const rows = useMemo(() => {
    const arr = (items || []).slice();
    arr.sort((a, b) => (SEV_ORDER[b.severity] || 1) - (SEV_ORDER[a.severity] || 1) || new Date(lastAt(b)) - new Date(lastAt(a)));
    return arr;
  }, [items]);

  const INP = "bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs outline-none focus:border-[#17A2B8]";
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div data-testid="alert-triage-view">
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="triage-filters">
        {companies.length > 1 && (
          <select value={fCompany} onChange={(e) => setFCompany(e.target.value)} data-testid="triage-filter-company" className={INP}>
            <option value="">Toutes les sociétés</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select value={fPool} onChange={(e) => setFPool(e.target.value)} data-testid="triage-filter-pool" className={INP}>
          <option value="">Tous les groupes</option>
          {pools.filter((p) => !fCompany || p.company_id === fCompany).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} data-testid="triage-filter-vehicle" className={INP}>
          <option value="">Tous les véhicules</option>
          {vehicles.filter((v) => !fCompany || v.company_id === fCompany).map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}
        </select>
        <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="triage-filter-type" className={INP}>
          <option value="">Tous les types</option>
          {Object.entries(EP_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={() => setMoreFilters((m) => !m)} data-testid="triage-more-filters" className="flex items-center gap-1.5 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-3 py-2 rounded-md text-xs transition-colors">
          <SlidersHorizontal className="w-3.5 h-3.5" /> Plus de filtres
        </button>
        <button onClick={load} data-testid="triage-refresh" className="flex items-center gap-1.5 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-3 py-2 rounded-md text-xs transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </button>
      </div>
      {moreFilters && (
        <div className="flex items-center gap-2 flex-wrap mb-4 px-3 py-2.5 border border-border rounded-lg bg-secondary/30" data-testid="triage-more-filters-panel">
          <select value={fSeverity} onChange={(e) => setFSeverity(e.target.value)} data-testid="triage-filter-severity" className={INP}>
            <option value="">Toutes les priorités</option>
            <option value="high">Critique</option>
            <option value="medium">Moyen</option>
            <option value="low">Info</option>
          </select>
          <select value={fAck} onChange={(e) => setFAck(e.target.value)} data-testid="triage-filter-ack" className={INP}>
            <option value="all">Acquittées + non</option>
            <option value="false">Non acquittées</option>
            <option value="true">Acquittées</option>
          </select>
        </div>
      )}

      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {loading ? (
          <div className="flex justify-center py-14" data-testid="triage-loading"><Loader2 className="w-6 h-6 animate-spin text-[#17A2B8]" /></div>
        ) : error ? (
          <div className="text-center py-12 px-6 text-sm text-[#EF4444] flex flex-col items-center gap-2" data-testid="triage-error">
            <AlertCircle className="w-8 h-8" /> Impossible de charger les alertes à traiter.
            <button onClick={load} className="mt-2 text-[#17A2B8] hover:underline">Réessayer</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-14 px-6 text-sm text-muted-foreground flex flex-col items-center gap-3" data-testid="triage-empty">
            <ShieldCheck className="w-10 h-10 text-[#10B981]/70" />
            <p className="font-semibold text-foreground">Aucune alerte à traiter</p>
            <p className="text-xs">Toutes les situations en cours sont traitées.</p>
            {onGoHistory && <button onClick={onGoHistory} data-testid="triage-go-history" className="mt-1 text-[#17A2B8] font-semibold hover:underline flex items-center gap-1">Consulter l'historique <ChevronRight className="w-3.5 h-3.5" /></button>}
          </div>
        ) : (
          <div className="divide-y divide-border" data-testid="triage-list">
            {rows.map((ep) => {
              const sev = SEVERITY[ep.severity] || SEVERITY.low;
              const open = !!expanded[ep.id];
              const occ = ep.occurrence_count || 1;
              const repeated = occ > 1;
              return (
                <div key={ep.id} data-testid={`triage-ep-${ep.id}`}>
                  <div onClick={() => toggle(ep.id)} className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-black/5" data-testid={`triage-ep-row-${ep.id}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: sev.color }} title={sev.label} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm">{ep.vehicle}</span>
                        <span className="text-xs text-muted-foreground">· {ep.plate}</span>
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full text-[#17A2B8] bg-[#17A2B8]/15">en cours</span>
                        {repeated && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#F5821F]/15 text-[#F5821F]" data-testid={`triage-occ-${ep.id}`} title="Nombre de déclenchements de cette situation">×{occ}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {typeLabel(ep.type)}{ep.rule_name ? ` · ${ep.rule_name}` : ""} · {fmtTimeTz(lastAt(ep), ep.company_timezone)}
                        <span className="ml-1">({timeAgo(lastAt(ep))})</span>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[11px] font-bold shrink-0 hidden sm:inline" style={{ color: sev.color, background: `${sev.color}22`, border: `1px solid ${sev.color}55` }}>{sev.label}</span>
                    {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </div>
                  {open && (
                    <div className="bg-secondary/20 px-4 py-3 pl-10 text-xs space-y-1.5" data-testid={`triage-ep-detail-${ep.id}`}>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-1 gap-x-4">
                        <div><span className="text-muted-foreground">Première détection</span><div className="font-semibold">{fmtTimeTz(ep.started_at, ep.company_timezone)}</div></div>
                        <div><span className="text-muted-foreground">Dernière activité</span><div className="font-semibold">{fmtTimeTz(lastAt(ep), ep.company_timezone)}</div></div>
                        <div><span className="text-muted-foreground">Déclenchements</span><div className="font-semibold">{occ}</div></div>
                        <div><span className="text-muted-foreground">Mesure</span><div className="font-semibold">{valSummary(ep)}</div></div>
                      </div>
                      {ep.message && <p className="text-muted-foreground">{ep.message}</p>}
                      <div className="flex items-center gap-4 pt-1">
                        <button onClick={(e) => { e.stopPropagation(); setDetail(ep); }} data-testid={`triage-open-detail-${ep.id}`} className="text-[#17A2B8] hover:underline font-semibold">Détail complet</button>
                        {!ep.acknowledged && canAck && <button onClick={(e) => ack(ep, e)} data-testid={`triage-ack-${ep.id}`} className="text-[#17A2B8] hover:underline font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Acquitter</button>}
                        {ep.acknowledged && <span className="text-muted-foreground">Acquittée</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {detail && <EpisodeDrawer ep={detail} onClose={() => setDetail(null)} canAck={canAck} onAck={(e) => ack(detail, e)} />}
    </div>
  );
}
